import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import HeroBanner from '../components/HeroBanner';
import PageTransition from '../components/Motion/PageTransition';

/**
 * Home Page - MVP landing experience with smooth animations
 * User Story 1 (P1): Cohesive Homepage Discovery
 * Respects prefers-reduced-motion via Framer Motion's built-in support
 */
function Home() {
  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <PageTransition>
      <HeroBanner />

      {/* Value Proposition Section */}
      <motion.section
        className="py-16 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Real Value Group?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We specialize in transforming complex business data into clear, actionable insights
              that drive growth and efficiency. Our team combines deep technical expertise with
              practical business acumen to deliver solutions that make a real impact.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Quick Links Section */}
      <section className="py-16 bg-background-light">
        <motion.div
          className="container mx-auto px-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300"
              variants={cardVariants}
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-semibold text-primary mb-3">Our Services</h3>
              <p className="text-gray-600 mb-4">
                Discover our comprehensive suite of data analytics and business intelligence services.
              </p>
              <Link
                to="/services"
                className="text-primary font-medium hover:underline focus-visible-ring"
              >
                Explore Services →
              </Link>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300"
              variants={cardVariants}
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-semibold text-primary mb-3">Our Projects</h3>
              <p className="text-gray-600 mb-4">
                See how we've helped businesses like yours achieve measurable results.
              </p>
              <Link
                to="/projects"
                className="text-primary font-medium hover:underline focus-visible-ring"
              >
                View Projects →
              </Link>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300"
              variants={cardVariants}
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-semibold text-primary mb-3">Get in Touch</h3>
              <p className="text-gray-600 mb-4">
                Ready to transform your data? Let's start a conversation about your needs.
              </p>
              <Link
                to="/contact"
                className="text-primary font-medium hover:underline focus-visible-ring"
              >
                Contact Us →
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </PageTransition>
  );
}

export default Home;

